/**
 * Post-login return path.
 *
 * A 401 clears cookies and bounces to /login, which previously lost wherever the
 * user was headed — every login landed on / or /setup-store. That breaks any
 * deep link mailed to someone whose session has lapsed, which is the normal case
 * for email: the run feedback links in digest emails would all dump the reader
 * on the dashboard home.
 *
 * Only same-origin relative paths are ever honoured. A return-URL parameter that
 * accepts absolute or protocol-relative values is an open redirect, which turns
 * our login page into a credible phishing launch point.
 */
const STORAGE_KEY = "cf_return_to";

/** Paths that would bounce the user straight back out again. */
const NON_RETURNABLE = new Set(["/login", "/signup"]);

export function isSafeReturnPath(value) {
    if (typeof value !== "string" || value === "") return false;

    // Must be a rooted relative path: "/foo". Anything else can leave the origin.
    if (!value.startsWith("/")) return false;

    // "//host" and "/\host" are protocol-relative — browsers resolve both to an
    // external origin despite the leading slash.
    if (value.startsWith("//") || value.startsWith("/\\")) return false;

    // Control characters can smuggle a second header or confuse URL parsing.
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001F\u007F]/.test(value)) return false;

    const [path] = value.split(/[?#]/);
    if (NON_RETURNABLE.has(path)) return false;

    return true;
}

/** Record where the user was, before a 401 redirect throws it away. */
export function rememberReturnPath(path) {
    if (!isSafeReturnPath(path)) return false;
    try {
        window.sessionStorage.setItem(STORAGE_KEY, path);
        return true;
    } catch {
        // Private browsing and storage-partitioned contexts can throw. Losing the
        // return path is a degraded login, not a broken one.
        return false;
    }
}

/** Read and clear the stored path. Re-validated on the way out. */
export function consumeReturnPath() {
    let stored = null;
    try {
        stored = window.sessionStorage.getItem(STORAGE_KEY);
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        return null;
    }
    return isSafeReturnPath(stored) ? stored : null;
}

export const RETURN_PATH_STORAGE_KEY = STORAGE_KEY;
